import { Immutable } from '@zervant/reactcomponents'
import { Attachment, ExpenseData, ExpenseTransaction } from 'client/Interfaces/Expenses/ExpensesInterface'
import { StateInterface } from 'client/Interfaces/StateInterface'

import { ExpenseAction } from '../actions/actionTypes'
import { ReducerBuilder } from './ReducerBuilder'

const INITIAL_STATE = {
  bankRequestStatus: null,
  transactionsMap: {},
  transactions: [],
  loaded: false,
  deletedElements: null,
  totalAmount: null,
  totalElements: null,
  elementCountWithoutAttachment: null,
}

export interface ExpenseReportSummary {
  oneMonthSummary: number
  twelveMonthSummary
}

export interface ExpensesState {
  bankRequestStatus: string
  transactionsMap: Record<string, ExpenseTransaction[]>
  transactions: ExpenseTransaction[]
  loaded: boolean
  size?: string
  nextPage?: string
  lastPage?: boolean
  totalAmount: string
  totalElements: string
  elementCountWithoutAttachment: string
  deletedElements?: string
  summary?: ExpenseReportSummary
}

type PayloadType = ExpenseData | Attachment | string | ExpenseTransaction | ExpenseReportSummary | string[]

const flattenMap = (map: Record<string, ExpenseTransaction[]>): ExpenseTransaction[] => {
  let transactions = [] as ExpenseTransaction[]
  if (map && Object.keys(map)?.length > 0) {
    for (const [key, value] of Object.entries(map)) {
      transactions = transactions.concat(value)
    }
  }
  return transactions
}

const getExpenses = (state: ExpensesState, payload: ExpenseData): ExpensesState => {
  const data = Immutable.copy(payload)
  if (payload && payload?.transactions?.length > 0) {
    const {
      page,
      transactions,
      bankRequestStatus,
      totalAmount,
      totalElements,
      elementCountWithoutAttachment,
      deletedElements,
    } = data
    const map = { ...state.transactionsMap }
    map[page] = transactions
    let newTransactions = flattenMap(map).filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i)
    newTransactions = newTransactions?.filter((t) => t !== null)
    const newState = {
      ...state,
      transactionsMap: map,
      loaded: true,
      transactions: newTransactions,
      bankRequestStatus,
      totalAmount,
      totalElements,
      deletedElements,
      elementCountWithoutAttachment,
    }
    return newState
  } else if (payload && payload?.transactions?.length === 0) {
    const newState = { ...state, loaded: true }
    return newState
  }
  return state
}

const updateExpense = (state: ExpensesState, payload: ExpenseTransaction): ExpensesState => {
  const newState = Immutable.copy(state)
  if (payload && payload?.id) {
    const { transactions } = state
    let newTransactions = transactions
    const index = transactions?.findIndex((t) => t?.id === payload?.id)
    newState.totalAmount = String(
      Number(newState.totalAmount) +
        (payload.transactionAmount.amount - newTransactions[index].transactionAmount.amount),
    )
    payload.attachmentLoading = transactions[index]?.attachmentLoading
    payload.attachments = transactions[index]?.attachments
    newTransactions[index] = payload
    newTransactions = newTransactions?.filter((t) => t !== null)
    newState.transactions = newTransactions
    return newState
  }
  return state
}

const saveExpense = (state: ExpensesState, payload: ExpenseTransaction): ExpensesState => {
  const newState = Immutable.copy(state)
  if (payload) {
    const { transactions } = state
    const newTransactions = transactions
    newTransactions.unshift(payload)
    const index = newTransactions?.findIndex((t) => t?.id === payload?.id)
    newState.transactions = newTransactions
    newState.loaded = true
    newState.totalElements = String(Number(newState.totalElements) + 1)
    newState.totalAmount = String((Number(newState.totalAmount) + payload.transactionAmount.amount).toFixed(2))
    newState.elementCountWithoutAttachment =
      newTransactions[index].attachments.length > 0
        ? newState.elementCountWithoutAttachment
        : String(Number(newState.elementCountWithoutAttachment) + 1)
    newState.transactionsMap[0] = [payload].concat(newState.transactionsMap[0])
    return newState
  }
  return state
}

const deleteExpense = (state: ExpensesState, payload: string): ExpensesState => {
  const newState = Immutable.copy(state)
  if (payload) {
    const { transactions } = newState
    let newTransactions = transactions.filter((t) => t?.id !== payload)
    const index = transactions?.findIndex((t) => t?.id === payload)
    newTransactions = newTransactions.filter((t) => t !== null)
    newState.transactions = newTransactions
    newState.deletedElements = String(Number(newState.deletedElements) + 1)
    newState.totalElements = String(Number(newState.totalElements) - 1)
    newState.elementCountWithoutAttachment =
      transactions[index]?.attachments?.length > 0
        ? newState.elementCountWithoutAttachment
        : String(Number(newState?.elementCountWithoutAttachment) - 1)
    newState.totalAmount = String(
      (Number(newState?.totalAmount) - transactions[index]?.transactionAmount?.amount).toFixed(2),
    )
    return newState
  }
  return state
}

const deleteAllExpenses = (state: ExpensesState, payload: string[]): ExpensesState => {
  const newState = Immutable.copy(state)
  if (payload && payload.length > 0) {
    const { transactions } = newState
    const count = payload.length
    let newTransactions = transactions.filter((t) => !payload.includes(t?.id))
    newState.transactions = newTransactions
    newTransactions = newTransactions.filter((t) => t !== null)
    newState.deletedElements = String(Number(newState.deletedElements) + count)
    newState.totalElements = String(Number(newState.totalElements) - count)

    // Count for expenses without attachment
    const initialElementCountWithoutAttachment = Number(newState.elementCountWithoutAttachment)
    const elementCountWithoutAttachment = transactions
      .filter((t) => payload.includes(t?.id))
      .reduce(
        (accumulator, t) => (t?.attachments?.length > 0 ? accumulator - 1 : accumulator),
        initialElementCountWithoutAttachment,
      )
    newState.elementCountWithoutAttachment = String(elementCountWithoutAttachment)

    // Count for expenses total amount
    const totalAmount = transactions
      .filter((t) => payload.includes(t?.id))
      .reduce((accumulator, t) => accumulator - t.transactionAmount.amount, Number(newState?.totalAmount))
      .toFixed(2)
    newState.totalAmount = String(totalAmount)
    return newState
  }
  return state
}

const restoreAllExpenses = (state: ExpensesState, payload: string[]): ExpensesState => {
  const newState = Immutable.copy(state)
  if (payload && payload.length > 0) {
    const { transactions } = newState
    const count = payload.length
    let newTransactions = transactions.filter((t) => !payload.includes(t?.id))
    newState.transactions = newTransactions
    newTransactions = newTransactions.filter((t) => t !== null)
    newState.deletedElements = String(Number(newState.deletedElements) - count)
    newState.totalElements = String(Number(newState.totalElements) + count)
    // Count for expenses without attachment
    const initialElementCountWithoutAttachment = Number(newState.elementCountWithoutAttachment)
    const elementCountWithoutAttachment = transactions
      .filter((t) => payload.includes(t?.id))
      .reduce(
        (accumulator, t) => (t?.attachments?.length > 0 ? accumulator + 1 : accumulator),
        initialElementCountWithoutAttachment,
      )
    newState.elementCountWithoutAttachment = String(elementCountWithoutAttachment)

    // Count for expenses total amount
    const totalAmount = transactions
      .filter((t) => payload.includes(t?.id))
      .reduce((accumulator, t) => accumulator + t.transactionAmount.amount, Number(newState?.totalAmount))
      .toFixed(2)
    newState.totalAmount = String(totalAmount)
    return newState
  }
  return state
}

const unDeleteExpense = (state: ExpensesState, payload: string): ExpensesState => {
  const newState = Immutable.copy(state)
  if (payload) {
    const { transactions } = newState
    let newTransactions = transactions.filter((t) => t?.id !== payload)
    newTransactions = newTransactions.filter((t) => t !== null)
    newState.transactions = newTransactions
    const index = newTransactions?.findIndex((t) => t?.id === payload)
    newState.deletedElements = String(Number(newState.deletedElements) - 1)
    newState.elementCountWithoutAttachment =
      newTransactions[index]?.attachments?.length > 0
        ? newState.elementCountWithoutAttachment
        : String(Number(newState?.elementCountWithoutAttachment) + 1)
    newState.totalElements = String(Number(newState?.totalElements) + 1)
    newState.totalAmount = String(
      (Number(newState?.totalAmount) + newTransactions[index]?.transactionAmount?.amount).toFixed(2),
    )
  }
  return newState
}

const resetExpenses = (state: ExpensesState): ExpensesState => {
  const newState = Immutable.copy(state)
  newState.transactions = []
  newState.transactionsMap = {}
  newState.loaded = false
  return newState
}

const getAttachment = (state: ExpensesState, payload: Attachment): ExpensesState => {
  const newState = Immutable.copy(state)
  if (payload && payload?.attachmentStatus === 'SUCCESS') {
    const { transactionId } = payload
    let newTransactions = newState?.transactions
    const index = newState?.transactions?.findIndex((t) => t?.id === transactionId)
    newTransactions[index].attachments = [payload]
    newTransactions[index].attachmentLoading = false
    newTransactions = newTransactions.filter((t) => t !== null)
    newState.transactions = newTransactions
  }
  return newState
}

const atttachmentLoading = (state: ExpensesState, payload: ExpenseTransaction): ExpensesState => {
  const newState = Immutable.copy(state)
  const newTransactions = newState.transactions.map((transaction) => ({
    ...transaction,
    attachmentLoading: transaction?.id === payload?.id ? true : transaction.attachmentLoading,
  }))
  newState.transactions = newTransactions
  return newState
}

const uploadAttachment = (state: ExpensesState, payload: Attachment): ExpensesState => {
  const newState = Immutable.copy(state)
  const { transactionId } = payload
  let newTransactions = newState?.transactions
  const index = newState?.transactions?.findIndex((t) => t?.id === transactionId)
  newTransactions[index].attachments = payload?.attachmentStatus === 'SUCCESS' ? [payload] : []
  newTransactions[index].attachmentLoading = false
  newTransactions = newTransactions.filter((t) => t !== null)
  newState.elementCountWithoutAttachment = String(Number(newState.elementCountWithoutAttachment) - 1)
  newState.transactions = newTransactions

  return newState
}

const deleteAttachment = (state: ExpensesState, payload: Attachment): ExpensesState => {
  const newState = Immutable.copy(state)
  const newTransactions = newState?.transactions
  const { transactionId } = payload
  const index = newState?.transactions?.findIndex((t) => t?.id === transactionId)
  newState.elementCountWithoutAttachment = String(Number(newState.elementCountWithoutAttachment) + 1)
  newTransactions[index].attachments = []
  return newState
}

const setLoading = (state: ExpensesState, payload: string): ExpensesState => {
  const newState = Immutable.copy(state)
  newState.loaded = false
  return newState
}

const getReportSummary = (state: ExpensesState, payload: ExpenseReportSummary): ExpensesState => {
  const newState = Immutable.copy(state)
  newState.summary = payload
  return newState
}

const reducer = ReducerBuilder.create<ExpensesState, PayloadType>(INITIAL_STATE)
  .mapAction(ExpenseAction.GET_EXPENSES, getExpenses)
  .mapAction(ExpenseAction.UPDATE_EXPENSE, updateExpense)
  .mapAction(ExpenseAction.SAVE_EXPENSE, saveExpense)
  .mapAction(ExpenseAction.DELETE_EXPENSE, deleteExpense)
  .mapAction(ExpenseAction.DELETE_ALL_EXPENSES, deleteAllExpenses)
  .mapAction(ExpenseAction.UNDELETE_EXPENSE, unDeleteExpense)
  .mapAction(ExpenseAction.RESTORE_ALL_EXPENSES, restoreAllExpenses)
  .mapAction(ExpenseAction.RESET_EXPENSES, resetExpenses)
  .mapAction(ExpenseAction.GET_ATTACHMENT, getAttachment)
  .mapAction(ExpenseAction.UPLOAD_ATTACHMENT, uploadAttachment)
  .mapAction(ExpenseAction.ATTACHMENT_LOADING, atttachmentLoading)
  .mapAction(ExpenseAction.DELETE_ATTACHMENT, deleteAttachment)
  .mapAction(ExpenseAction.SET_LOADING, setLoading)
  .mapAction(ExpenseAction.GET_REPORT_SUMMARY, getReportSummary)
  .build()

export default reducer

export const expensesSelector = (state: StateInterface): ExpensesState => state.expenses
